import { useState, useCallback } from "react";
import { useDocumentViewport } from "@/features/document-viewport/context";
import type { Point } from "@/lib/geometry";
import type { CalibrationState } from "@/hooks/useMeasurementScale";

interface CalibrationOverlayProps {
  calibrationState: CalibrationState;
  isCalibrationMode: boolean;
  onViewportClick: (point: Point) => void;
  onUpdatePoint1: (point: Point) => void;
  onUpdatePoint2: (point: Point) => void;
}

export function CalibrationOverlay({
  calibrationState,
  isCalibrationMode,
  onViewportClick,
  onUpdatePoint1,
  onUpdatePoint2,
}: CalibrationOverlayProps) {
  const viewport = useDocumentViewport();
  const [draggingPoint, setDraggingPoint] = useState<1 | 2 | null>(null);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isCalibrationMode) return;
    if (calibrationState.phase !== "first_point_set" && calibrationState.phase !== "idle") {
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;

    const pagePoint = viewport.transforms.screenToPagePoint({ x: screenX, y: screenY });
    if (!pagePoint) return;

    onViewportClick({ x: pagePoint.x, y: pagePoint.y });
  };

  const handleMouseDown = (pointNum: 1 | 2) => (e: React.MouseEvent<HTMLDivElement>) => {
    if (calibrationState.phase !== "second_point_set") return;
    e.stopPropagation();
    setDraggingPoint(pointNum);
  };

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!draggingPoint || calibrationState.phase !== "second_point_set") return;

    const rect = e.currentTarget.getBoundingClientRect();
    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;

    const pagePoint = viewport.transforms.screenToPagePoint({ x: screenX, y: screenY });
    if (!pagePoint) return;

    if (draggingPoint === 1) {
      onUpdatePoint1({ x: pagePoint.x, y: pagePoint.y });
    } else if (draggingPoint === 2) {
      onUpdatePoint2({ x: pagePoint.x, y: pagePoint.y });
    }
  }, [draggingPoint, calibrationState.phase, viewport, onUpdatePoint1, onUpdatePoint2]);

  const handleMouseUp = useCallback(() => {
    setDraggingPoint(null);
  }, []);

  // Convert page coordinates to screen coordinates for rendering
  const getScreenPoint = (pagePoint: Point) => {
    return viewport.transforms.pageToScreenPoint(pagePoint);
  };

  // Only render when in calibration mode
  if (!isCalibrationMode) return null;

  return (
    <div
      className="absolute inset-0"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {/* Click catcher - active during idle and first_point_set phases */}
      {(calibrationState.phase === "idle" || calibrationState.phase === "first_point_set") && (
        <div
          className="absolute inset-0 z-30 cursor-crosshair"
          onClick={handleClick}
        />
      )}
      
      {/* Visual markers */}
      {calibrationState.phase === "first_point_set" && (
        <CalibrationMarker
          point={getScreenPoint(calibrationState.point1)}
          label="1"
        />
      )}
      
      {calibrationState.phase === "second_point_set" && (
        <>
          <CalibrationMarker
            point={getScreenPoint(calibrationState.point1)}
            label="1"
            isDraggable
            onMouseDown={handleMouseDown(1)}
          />
          <CalibrationMarker
            point={getScreenPoint(calibrationState.point2)}
            label="2"
            isDraggable
            onMouseDown={handleMouseDown(2)}
          />
          <CalibrationLine
            point1={getScreenPoint(calibrationState.point1)}
            point2={getScreenPoint(calibrationState.point2)}
            pixelDistance={calibrationState.pixelDistance}
          />
        </>
      )}
    </div>
  );
}

interface CalibrationMarkerProps {
  point: { x: number; y: number };
  label: string;
  isDraggable?: boolean;
  onMouseDown?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

function CalibrationMarker({ point, label, isDraggable, onMouseDown }: CalibrationMarkerProps) {
  return (
    <div
      className={`absolute z-40 ${isDraggable ? 'cursor-move' : 'pointer-events-none'}`}
      style={{
        left: point.x - 8,
        top: point.y - 8,
        width: 16,
        height: 16,
      }}
      onMouseDown={onMouseDown}
    >
      <div className="relative w-full h-full">
        {/* Crosshair */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-full h-0.5 bg-blue-500" />
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-0.5 h-full bg-blue-500" />
        </div>
        {/* Label badge */}
        <div className="absolute -top-5 -left-1 bg-blue-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
          {label}
        </div>
      </div>
    </div>
  );
}

function CalibrationLine({
  point1,
  point2,
  pixelDistance,
}: {
  point1: { x: number; y: number };
  point2: { x: number; y: number };
  pixelDistance: number;
}) {
  const dx = point2.x - point1.x;
  const dy = point2.y - point1.y;
  const length = Math.sqrt(dx * dx + dy * dy);
  const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
  
  // Position the line at the midpoint for the label
  const midX = (point1.x + point2.x) / 2;
  const midY = (point1.y + point2.y) / 2;

  return (
    <>
      {/* Line */}
      <div
        className="absolute z-[35] pointer-events-none bg-blue-500/80"
        style={{
          left: point1.x,
          top: point1.y - 1,
          width: length,
          height: 2,
          transformOrigin: "0 50%",
          transform: `rotate(${angle}deg)`,
        }}
      />
      
      {/* Distance label */}
      <div
        className="absolute z-[45] pointer-events-none bg-blue-500 text-white text-xs font-medium px-2 py-1 rounded shadow-lg"
        style={{
          left: midX,
          top: midY - 20,
          transform: "translateX(-50%)",
        }}
      >
        {Math.round(pixelDistance)} px
      </div>
    </>
  );
}

export type { CalibrationOverlayProps };
